<template>
  <canvas ref="canvas" class="three-bg" />
</template>

<script>
import * as THREE from 'three'

export default {
  name: 'ThreeBackground',
  data() {
    return { animId: null }
  },
  mounted() {
    this.initScene()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    cancelAnimationFrame(this.animId)
    window.removeEventListener('resize', this.onResize)
    if (this.renderer) this.renderer.dispose()
  },
  methods: {
    initScene() {
      const canvas = this.$refs.canvas
      const w = window.innerWidth
      const h = window.innerHeight

      this.scene = new THREE.Scene()
      this.camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 100)
      this.camera.position.z = 20

      this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
      this.renderer.setSize(w, h)
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.renderer.setClearColor(0x000000, 0)

      const goldColor = 0xD0B092
      const goldDark = 0xC9A78A
      const accentColor = 0x7367f0

      this.meshes = []

      const material = (color, opacity) => new THREE.MeshPhongMaterial({
        color,
        transparent: true,
        opacity,
        shininess: 80,
      })

      const shapes = [
        { geo: new THREE.TorusGeometry(1.5, 0.4, 16, 40), mat: material(goldColor, 0.12), pos: [-8, 6, -5], speed: [0.003, 0.005] },
        { geo: new THREE.OctahedronGeometry(1.2), mat: material(accentColor, 0.08), pos: [10, -4, -8], speed: [0.004, 0.002] },
        { geo: new THREE.IcosahedronGeometry(1), mat: material(goldDark, 0.1), pos: [-6, -7, -3], speed: [0.005, 0.003] },
        { geo: new THREE.TorusKnotGeometry(0.8, 0.25, 64, 8), mat: material(goldColor, 0.07), pos: [7, 5, -10], speed: [0.002, 0.004] },
        { geo: new THREE.SphereGeometry(1.8, 32, 32), mat: material(accentColor, 0.05), pos: [-3, 0, -12], speed: [0.001, 0.003] },
        { geo: new THREE.DodecahedronGeometry(0.9), mat: material(goldDark, 0.09), pos: [5, -8, -6], speed: [0.006, 0.002] },
        { geo: new THREE.TorusGeometry(1, 0.3, 12, 30), mat: material(goldColor, 0.06), pos: [12, 2, -14], speed: [0.003, 0.001] },
        { geo: new THREE.OctahedronGeometry(0.7), mat: material(accentColor, 0.07), pos: [-10, -3, -9], speed: [0.002, 0.006] },
      ]

      shapes.forEach(s => {
        const mesh = new THREE.Mesh(s.geo, s.mat)
        mesh.position.set(...s.pos)
        mesh.userData.speed = s.speed
        mesh.userData.baseY = s.pos[1]
        this.scene.add(mesh)
        this.meshes.push(mesh)
      })

      const ambient = new THREE.AmbientLight(0xffffff, 0.4)
      this.scene.add(ambient)

      const point = new THREE.PointLight(0xD0B092, 0.6, 50)
      point.position.set(5, 10, 10)
      this.scene.add(point)

      const point2 = new THREE.PointLight(0x7367f0, 0.3, 50)
      point2.position.set(-10, -5, 8)
      this.scene.add(point2)

      this.clock = new THREE.Clock()
      this.animate()
    },

    animate() {
      this.animId = requestAnimationFrame(this.animate)
      const t = this.clock.getElapsedTime()

      this.meshes.forEach(mesh => {
        mesh.rotation.x += mesh.userData.speed[0]
        mesh.rotation.y += mesh.userData.speed[1]
        mesh.position.y = mesh.userData.baseY + Math.sin(t * 0.5 + mesh.position.x) * 0.5
      })

      this.renderer.render(this.scene, this.camera)
    },

    onResize() {
      const w = window.innerWidth
      const h = window.innerHeight
      this.camera.aspect = w / h
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(w, h)
    },
  },
}
</script>

<style scoped>
.three-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
</style>
